import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormArray, FormBuilder, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';
import { FileUploadService } from 'app/common/services/fileUpload.service';
import { DocumentGridComponent } from 'app/profile/components/document/document-grid/document-grid.component';
import { DocumentType, SupplierDocument, MultiDocument } from 'app/profile/models/supplier.model';
import { OnboardingDocumentsService } from 'app/profile/services/onboarding-document.service';
import { SupplierStateService } from 'app/profile/services/supplierstate.service';
import { GetDocumentType } from 'app/profile/store/app.actions';
import { GetCurrentSupplierCode, SelectDocumentTypes } from 'app/profile/store/app.selector';
import { SupplierState } from 'app/profile/store/state/supplierstate.state';
import { CustomValidator } from 'app/shared/custom-validator/custom-validator';
import { I18nService } from 'app/shared/services/i18n.service';
import { IncrementLoaderCount, DecrementLoaderCount } from 'app/shared/store/actions';
import { isNotNullOrUndefined, validateAllFormFields } from 'app/shared/utils/utils';
import * as FileSaver from 'file-saver';
import { Subscription } from 'rxjs';
import { isNullOrUndefined } from 'util';

@Component({
  selector: 'diversity-document-grid',
  templateUrl: './diversity-document-grid.component.html',
  styleUrls: ['./diversity-document-grid.component.scss']
})
export class DiversityDocumentGridComponent implements OnInit, OnDestroy {
  requiredDocuments: MultiDocument[] = [];
  documentTypes: DocumentType[] = [];
  formGroup: FormGroup;
  requiredDocumentsFormArray: FormArray;

  supplierCode: string = '';
  fileUploadSub: Subscription;


  get Value(): SupplierDocument[] {
    const result: SupplierDocument[] = [];
    for (let index = 0; index < this.requiredDocumentsFormArray.length; index++) {
      const element: MultiDocument = (this.requiredDocumentsFormArray.controls[index] as FormGroup).getRawValue();
      if (element.Attachments.length > 0) {
        for (let i = 0; i < element.Attachments.length; i++) {
          const attachment = element.Attachments[i];
          attachment.DocumentType = element.DocumentType;
          attachment.File = null;
          attachment.EffectiveDate = element.EffectiveDate;
          attachment.ExpiryDate = element.ExpiryDate;
          result.push(attachment);
        }
      }

    }
    return result;
  }

  get IsValid() {
    if (this.requiredDocumentsFormArray.valid)
      return true;
    else
      validateAllFormFields(this.formGroup);
  }

  constructor(
    private fb: FormBuilder,
    private supplierStateService: SupplierStateService,
    private snackBar: MatSnackBar,
    private store: Store<SupplierState>,
    private fileUploadService: FileUploadService,
    private translate: I18nService
  ) {
    this.formGroup = this.fb.group({
      Documents: this.fb.array([])
    })
    this.requiredDocumentsFormArray = this.formGroup.get('Documents') as FormArray;
  }
  ngOnDestroy(): void {
    if (this.fileUploadSub) {
      this.fileUploadSub.unsubscribe();
    }
  }

  ngOnInit() {
    this.store.dispatch(new GetDocumentType());
    this.store.select(SelectDocumentTypes).subscribe(x => this.documentTypes = x);
    this.store.select(GetCurrentSupplierCode).subscribe(x => this.supplierCode = x);
  }

  upsertDocumentType(event: MultiDocument) {
    // when documenttype is not emtpy and if exists, update matched document
    if (this.requiredDocuments.some(x => x.DocumentType === event.DocumentType) && event.DocumentType) {
      this.updateDocumentType(event);
    }
    // otherwise append document
    else {
      this.addDocumentType(event);
    }
  }

  addDocumentType(event: MultiDocument) {
    this.requiredDocuments.push({ DocumentType: event.DocumentType, Attachments: event.Attachments, IsRequired: event.IsRequired });
    const grp = this.fb.group({
      DocumentType: [{ value: event.DocumentType, disabled: event.IsRequired }, [Validators.required]],
      Attachments: [event.Attachments ? event.Attachments : null, Validators.required],
      EffectiveDate: [event.EffectiveDate? event.EffectiveDate: null],
      ExpiryDate: [event.ExpiryDate ? event.ExpiryDate : null]
    });
    if (event.IsRequired) {
      grp.get('Attachments').setValidators([Validators.required]);
      grp.get('EffectiveDate').setValidators([Validators.required]);
      grp.get('ExpiryDate').setValidators([Validators.required]);
    } else {
      grp.get('Attachments').clearValidators();
      grp.get('EffectiveDate').clearValidators();
      grp.get('ExpiryDate').clearValidators();
    }
    grp.updateValueAndValidity();

    this.requiredDocumentsFormArray.push(grp);
    this.requiredDocumentsFormArray.updateValueAndValidity();
  }

  updateDocumentType(event: MultiDocument) {
    this.removeDocumentType(event.DocumentType);
    this.addDocumentType(event);
  }

  removeDocumentType(documentType: string) {
    const index = this.requiredDocuments.findIndex(x => x.DocumentType === documentType);
    if (index > -1) {
      this.requiredDocumentsFormArray.removeAt(index);
      this.requiredDocumentsFormArray.updateValueAndValidity();

      const i = this.requiredDocuments.findIndex(x => x.DocumentType === documentType);
      this.requiredDocuments.splice(i, 1);
    }
  }

  onFileAdd(index: number, files: File[]) {
    const supplierdocument = new SupplierDocument();
    supplierdocument.File = files[0];
    this.fileUploadSub = this.fileUploadService.uploadFileToServer([supplierdocument.File], this.supplierCode).subscribe(res => {
      supplierdocument.DocumentID = res['documentID'] || res.DocumentID;
      supplierdocument.DocumentVersion = res['documentVersion'] || res.DocumentVersion;
      supplierdocument.FileName = res['fileName'] || res.FileName;
      this.requiredDocumentsFormArray.controls[index].patchValue({
        Attachments: [supplierdocument]
      });
      this.requiredDocumentsFormArray.updateValueAndValidity();
    });
    // this.readFileContent(files[0]).then(r => supplierdocument.FileAsString = r);
  }

  removeFile(index: number) {
    this.requiredDocumentsFormArray.controls[index].patchValue({
      Attachments: null
    });
    this.requiredDocumentsFormArray.updateValueAndValidity();
  }

  downloadDocument(document: SupplierDocument) {
    document.ReferenceSystem = 'DMS'
    if (document && isNotNullOrUndefined(document.File)) {
      FileSaver.saveAs(document.File, document.FileName || document.DocumentID);
    } else {
      if (document && document.DocumentID) {
        this.store.dispatch(new IncrementLoaderCount());
        this.supplierStateService.downloadFile(this.supplierCode, document.ReferenceSystem, document.DocumentID, document.DocumentVersion)
          .subscribe(response => {
            this.store.dispatch(new DecrementLoaderCount());
            // this.eSignService.downloadFile(response, `${document.FileName}`);
            FileSaver.saveAs(response, document.FileName || document.DocumentID);
          }, (error) => {
            this.store.dispatch(new DecrementLoaderCount());
            this.snackBar.open(`Something went wrong. Unable to download!`, null, {
              duration: 5000,
              verticalPosition: 'top',
              panelClass: ['red-snackbar']
            });
          });
      }
    }
  }

  readFileContent(file: File): Promise<string> {
    return new Promise<string>((resolve) => {
      if (!file) {
        resolve('');
      }
      const reader = new FileReader();
      reader.onloadend = (e: any): any => {
        // remove base64 header
        const base64String = e.target.result.split(",")[1];
        resolve(base64String);
      };
      reader.readAsDataURL(file);
    });
  }
}
